import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, CheckCircle2, ShoppingCart, Gauge } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SEO } from '../components/shared/SEO';
export function ProductCompleteKit() {
  const features = ['Complete system with enclosed system housing', 'Plug-and-play electric power', '75-foot hose with trigger-jet wand', 'Draws solution directly from container', 'Automatic 60 PSI shutoff', 'Built for professional remediation and disinfecting'];
  return <div className="bg-white" data-id="element-361">
      <SEO title="ForceX Complete Kit | Precision Pro Sprayers" description="Out of the box, ready to work. Includes enclosed system housing for maximum efficiency and cleanliness." data-id="element-362" />

      {/* Product Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-id="element-363">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-364">
          {/* Image Column */}
          <div className="bg-gray-100 rounded-xl overflow-hidden aspect-square flex items-center justify-center border border-gray-200 relative" data-id="element-365">
            <img src="https://cdn.magicpatterns.com/uploads/ue1UHvVFctLn5M4uHM6oFK/IMG_6383.png" alt="ForceX Complete Kit" className="w-full h-full object-contain" data-id="element-366" />
            <div className="absolute top-6 right-6 bg-brand-yellow text-brand-dark font-bold px-4 py-2 rounded-full shadow-md flex items-center" data-id="element-367">
              <Star className="h-4 w-4 mr-2 fill-current" data-id="element-368" />
              Most Popular
            </div>
          </div>

          {/* Content Column */}
          <div data-id="element-369">
            <div className="mb-2" data-id="element-370">
              <span className="inline-block bg-brand-dark text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-371">
                Professional Choice
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-372">
              ForceX – Complete Kit
            </h1>
            <p className="text-xl text-brand-yellow font-bold mb-6" data-id="element-373">
              Out of the box. On the job. No compromises.
            </p>

            <div className="prose prose-lg text-gray-600 mb-8" data-id="element-374">
              <p data-id="element-375">
                The ForceX Complete Kit is a ready-to-work electric spraying
                system built for professional contractors tackling demanding
                environments. The included system housing eliminates pouring and
                reduces chemical exposure while maintaining fast setup and
                mobility.
              </p>
            </div>

            <div className="bg-brand-light p-6 rounded-lg mb-8 border border-gray-200" data-id="element-376">
              <h3 className="font-bold text-brand-dark mb-4 text-lg" data-id="element-377">
                Key Features
              </h3>
              <ul className="space-y-3" data-id="element-378">
                {features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-379">
                    <Check className="h-5 w-5 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-380" />
                    <span className="text-gray-700" data-id="element-381">{feature}</span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-center" data-id="element-382">
              <div className="text-3xl font-bold text-brand-dark" data-id="element-383">$749.99</div>
              <Link to="/contact" className="flex-grow sm:flex-grow-0" data-id="element-384">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" data-id="element-385">
                  Buy Now
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-386">
              *First-time purchaser discounts available. Contact us for bulk
              pricing.
            </p>
          </div>
        </div>
      </div>

      {/* Tech Specs / Details Band */}
      <div className="bg-brand-dark text-white py-16" data-id="element-387">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-388">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-389">
            <div className="p-4" data-id="element-390">
              <div className="text-brand-yellow font-bold text-4xl mb-2" data-id="element-391">
                60 PSI
              </div>
              <div className="text-gray-400" data-id="element-392">Automatic Shutoff</div>
            </div>
            <div className="p-4 border-y md:border-y-0 md:border-x border-gray-700" data-id="element-393">
              <div className="text-brand-yellow font-bold text-4xl mb-2" data-id="element-394">
                75 FT
              </div>
              <div className="text-gray-400" data-id="element-395">Reinforced Hose</div>
            </div>
            <div className="p-4" data-id="element-396">
              <div className="text-brand-yellow font-bold text-4xl mb-2" data-id="element-397">
                110V
              </div>
              <div className="text-gray-400" data-id="element-398">Corded Electric Power</div>
            </div>
          </div>
        </div>
      </div>

      {/* Automatic Pressure Control Explanation */}
      <section className="py-16 bg-white border-b border-gray-100" data-id="element-399">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <div className="flex flex-col md:flex-row items-start gap-8" data-id="element-401">
            <div className="flex-shrink-0" data-id="element-402">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-403">
                <Gauge className="h-8 w-8 text-brand-yellow" data-id="element-404" />
              </div>
            </div>
            <div data-id="element-405">
              <h2 className="text-2xl font-bold text-brand-dark mb-4" data-id="element-406">
                Automatic Pressure Control (60 PSI)
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed" data-id="element-407">
                The ForceX system automatically builds pressure up to 60 PSI and
                then shuts off. When the spray trigger is squeezed, pressure is
                released and the pump automatically reactivates to restore
                pressure on demand. This ensures consistent spray performance,
                prevents over-pressurization, and reduces wear on the system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white" data-id="element-408">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-409">
          <h2 className="text-3xl font-bold text-brand-dark mb-8" data-id="element-410">
            What's Included
          </h2>
          <div className="bg-brand-light p-8 rounded-xl border border-gray-200" data-id="element-411">
            <ul className="space-y-4" data-id="element-412">
              <li className="flex items-start" data-id="element-413">
                <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-414" />
                <span className="text-gray-700 text-lg" data-id="element-415">
                  Enclosed system housing
                </span>
              </li>
              <li className="flex items-start" data-id="element-416">
                <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-417" />
                <span className="text-gray-700 text-lg" data-id="element-418">
                  Plug-and-play electric pump
                </span>
              </li>
              <li className="flex items-start" data-id="element-419">
                <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-420" />
                <span className="text-gray-700 text-lg" data-id="element-421">75-foot hose</span>
              </li>
              <li className="flex items-start" data-id="element-422">
                <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-423" />
                <span className="text-gray-700 text-lg" data-id="element-424">
                  Adjustable trigger jet spray wand
                </span>
              </li>
              <li className="flex items-start" data-id="element-425">
                <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-3 mt-0.5 flex-shrink-0" data-id="element-426" />
                <span className="text-gray-700 text-lg" data-id="element-427">
                  External siphon intake line
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-brand-light" data-id="element-428">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-429">
          <h2 className="text-3xl font-bold text-brand-dark mb-8" data-id="element-430">
            Technical Specifications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-431">
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-432">
              <div className="font-bold text-brand-dark mb-2" data-id="element-433">Power</div>
              <div className="text-gray-600" data-id="element-434">
                Corded electric (no batteries)
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-435">
              <div className="font-bold text-brand-dark mb-2" data-id="element-436">Hose Length</div>
              <div className="text-gray-600" data-id="element-437">75 feet</div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-438">
              <div className="font-bold text-brand-dark mb-2" data-id="element-439">
                Spray Control
              </div>
              <div className="text-gray-600" data-id="element-440">Trigger-controlled wand</div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-441">
              <div className="font-bold text-brand-dark mb-2" data-id="element-442">Pressure</div>
              <div className="text-gray-600" data-id="element-443">Automatic shutoff at 60 PSI</div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-444">
              <div className="font-bold text-brand-dark mb-2" data-id="element-445">
                Intake System
              </div>
              <div className="text-gray-600" data-id="element-446">External siphon</div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-447">
              <div className="font-bold text-brand-dark mb-2" data-id="element-448">Applications</div>
              <div className="text-gray-600" data-id="element-449">
                Mold, disinfecting, antimicrobials, degreasers
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contractors Choose ForceX */}
      <section className="py-16 bg-white" data-id="element-450">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-451">
          <h2 className="text-3xl font-bold text-brand-dark mb-8" data-id="element-452">
            Why Contractors Choose ForceX
          </h2>
          <div className="space-y-4" data-id="element-453">
            <div className="flex items-start p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-454">
              <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-4 mt-0.5 flex-shrink-0" data-id="element-455" />
              <span className="text-gray-700 text-lg" data-id="element-456">
                No hauling chemicals into tight spaces
              </span>
            </div>
            <div className="flex items-start p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-457">
              <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-4 mt-0.5 flex-shrink-0" data-id="element-458" />
              <span className="text-gray-700 text-lg" data-id="element-459">
                No pouring into backpacks or small tanks
              </span>
            </div>
            <div className="flex items-start p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-460">
              <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-4 mt-0.5 flex-shrink-0" data-id="element-461" />
              <span className="text-gray-700 text-lg" data-id="element-462">
                No battery failures or downtime
              </span>
            </div>
            <div className="flex items-start p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-463">
              <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-4 mt-0.5 flex-shrink-0" data-id="element-464" />
              <span className="text-gray-700 text-lg" data-id="element-465">
                Faster setup and cleanup
              </span>
            </div>
            <div className="flex items-start p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-466">
              <CheckCircle2 className="h-6 w-6 text-brand-yellow mr-4 mt-0.5 flex-shrink-0" data-id="element-467" />
              <span className="text-gray-700 text-lg" data-id="element-468">
                Cleaner, more controlled spraying
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-brand-light" data-id="element-469">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-470">
          <h2 className="text-3xl font-bold text-brand-dark mb-4" data-id="element-471">
            Who It's For
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-472">Ideal for:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-473">
            {['Mold remediation professionals', 'Pest control', 'Janitorial and sanitation crews', 'Facilities maintenance teams', 'Property managers', 'Agricultural and horticultural use', 'Specialty surface treatment contractors'].map(audience => <div key={audience} className="flex items-center p-4 bg-white rounded-lg border border-gray-200" data-id="element-474">
                <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-3 flex-shrink-0" data-id="element-475" />
                <span className="text-gray-700" data-id="element-476">{audience}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-brand-dark text-white" data-id="element-477">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-478">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-479">
            Stop carrying chemicals where they don't belong.
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-480">
            Spray smarter with Precision Pro Sprayers.
          </p>
          <Link to="/contact" data-id="element-481">
            <Button variant="primary" size="lg" data-id="element-482">
              <ShoppingCart className="w-5 h-5 mr-2" data-id="element-483" />
              Add to Cart
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}